"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import { isProblemSolvingPage } from "@/lib/route-layout";

interface StickyTitleProps {
  title: string;
}

export default function StickyTitle({ title }: StickyTitleProps) {
  // 데스크탑 셸 레이아웃(UI 2.0)에서는 ContentHeader 가 본문 제목을 핀 고정하므로
  // 레거시 핀 제목을 숨겨 중복을 막는다 (JCG-2096). 클래식/모바일 레이아웃(inShell:false)
  // 에는 ContentHeader 가 없어 이 컴포넌트가 스크롤 시 제목을 계속 담당한다.
  // 정답 피드백은 레이아웃과 무관하게 AnswerFeedbackToast(sonner) 가 일원화 처리한다 (JCG-2112).
  const { inShell } = useArticleShellSlots();
  // 문제만 푸는 페이지(기출·모의고사·gamja-exam·감자퀴즈)에서만 모바일에서 핀 제목을 숨겨
  // 본문(문제)을 가리지 않게 한다 (JCG-2241). 일반 이론/코딩 읽기 글은 기존대로 모바일에서도 핀 제목 노출.
  const pathname = usePathname() ?? "";
  const hideOnMobile = isProblemSolvingPage(pathname);
  const stickyTitleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stickyTitle = stickyTitleRef.current;
    const originalTitle = stickyTitle?.closest('article')?.querySelector('h1');

    if (!originalTitle || !stickyTitle) return;

    let observer: IntersectionObserver;

    const createObserver = () => {
      observer?.disconnect();
      const navbarHeight = window.innerWidth >= 640 ? 64 : 48;
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            stickyTitle.classList.add('hidden');
          } else {
            stickyTitle.classList.remove('hidden');
          }
        },
        {
          rootMargin: `-${navbarHeight}px 0px 0px 0px`,
          threshold: 1.0,
        }
      );
      observer.observe(originalTitle);
    };

    createObserver();
    window.addEventListener('resize', createObserver);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', createObserver);
    };
  }, [inShell]);

  // 셸 레이아웃에서는 ContentHeader 가 핀 제목을 담당 → 렌더하지 않음
  if (inShell) return null;

  return (
    <div
      className={`fixed left-1/2 top-[var(--mobile-navbar-offset,var(--sticky-title-navbar-fallback))] z-40 hidden w-full max-w-[800px] -translate-x-1/2 bg-background/80 backdrop-blur-sm transition-[top] duration-200 ease-out motion-reduce:transition-none [--sticky-title-navbar-fallback:2.75rem] sm:[--sticky-title-navbar-fallback:4rem]${hideOnMobile ? " max-md:hidden" : ""}`}
      id="sticky-title"
      ref={stickyTitleRef}
    >
      <div className="px-2 sm:px-4 py-2">
        <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white line-clamp-1">
          {title}
        </div>
      </div>
    </div>
  );
}
